import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, ScanBarcode, Package, X, CheckCircle, Scissors, Plus, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const SPLIT_COLORS = ['bg-blue-50', 'bg-purple-50', 'bg-amber-50', 'bg-emerald-50', 'bg-rose-50'];

const Folding = () => {
  const navigate = useNavigate();
  const staffInputRef = useRef(null);
  const bundleInputRef = useRef(null);

  // Step: 'staff' | 'scan' | 'result'
  const [step, setStep] = useState('staff');

  // Multi-staff
  const [staffValue, setStaffValue] = useState('');
  const [staffList, setStaffList] = useState([]); // [{staff_id, staff_name}]
  const [validating, setValidating] = useState(false);

  // Bundles
  const [bundleScanValue, setBundleScanValue] = useState('');
  const [scannedBundles, setScannedBundles] = useState([]);
  const [scanning, setScanning] = useState(false);

  // Submit / Result
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const focusStaff = useCallback(() => setTimeout(() => staffInputRef.current?.focus(), 80), []);
  const focusBundle = useCallback(() => setTimeout(() => bundleInputRef.current?.focus(), 80), []);

  const handleAddStaff = async (e) => {
    e.preventDefault();
    const id = staffValue.trim();
    if (!id || validating) return;
    if (staffList.some((s) => s.staff_id === id)) {
      toast.error('Staff already added');
      setStaffValue('');
      focusStaff();
      return;
    }
    setValidating(true);
    try {
      const resp = await apiClient.post('/folding/validate-staff', { staff_id: id });
      setStaffList((prev) => [...prev, { staff_id: resp.data.staff_id, staff_name: resp.data.staff_name }]);
      setStaffValue('');
      toast.success(`Added ${resp.data.staff_name}`);
      focusStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid Staff ID');
    } finally {
      setValidating(false);
    }
  };

  const handleRemoveStaff = (staff_id) => {
    setStaffList((prev) => prev.filter((s) => s.staff_id !== staff_id));
  };

  const handleProceedToScan = () => {
    if (staffList.length === 0) {
      toast.error('Add at least one staff');
      return;
    }
    setStep('scan');
    focusBundle();
  };

  const handleBundleScan = async (e) => {
    e.preventDefault();
    const code = bundleScanValue.trim();
    if (!code) return;

    // Pre-flight dup check — catches scanning the SAME bundle by the
    // SAME code twice. The post-validate check below additionally
    // catches scanning the gate code first then the corrected
    // original_bundle (or vice-versa).
    if (scannedBundles.some((b) => b.bundle_code === code || b.scanned_as === code || b.original_bundle === code)) {
      toast.error(`Bundle ${code} already scanned`);
      setBundleScanValue('');
      return;
    }

    setScanning(true);
    try {
      const resp = await apiClient.get(`/folding/validate-bundle?bundle_code=${encodeURIComponent(code)}`);
      const canonical = resp.data.bundle_code;
      // Re-check after resolution — operator may have scanned the
      // corrected original number while the gate code is already on the list.
      if (scannedBundles.some((b) => b.bundle_code === canonical)) {
        toast.error(`Bundle ${canonical} already scanned`);
        return;
      }
      setScannedBundles((prev) => [...prev, resp.data]);
      setBundleScanValue('');
      focusBundle();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Bundle ${code} not eligible for folding`);
      setBundleScanValue('');
      focusBundle();
    } finally {
      setScanning(false);
    }
  };

  const removeBundleAt = (idx) => {
    setScannedBundles((prev) => prev.filter((_, i) => i !== idx));
  };

  const totalUsns = scannedBundles.reduce((sum, b) => sum + (b.usn_count || 0), 0);

  // Live qty split preview
  const splitPreview = staffList.length > 0
    ? staffList.map((s, i) => {
        const base = Math.floor(totalUsns / staffList.length);
        const remainder = totalUsns - base * staffList.length;
        return { ...s, qty: base + (i < remainder ? 1 : 0) };
      })
    : [];

  const handleSubmit = async () => {
    if (scannedBundles.length === 0) {
      toast.error('Scan at least one bundle');
      return;
    }
    if (staffList.length === 0) {
      toast.error('Add at least one staff');
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiClient.post('/folding/submit', {
        staff_list: staffList.map((s) => ({ staff_id: s.staff_id, staff_name: s.staff_name })),
        bundle_codes: scannedBundles.map((b) => b.bundle_code),
      });
      toast.success(res.data.message);
      setResult(res.data);
      setStep('result');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit folding');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setStep('staff');
    setStaffList([]);
    setStaffValue('');
    setScannedBundles([]);
    setResult(null);
  };

  const gridCols = (n) =>
    n === 1 ? 'grid-cols-1' :
    n === 2 ? 'grid-cols-2' :
    n === 3 ? 'grid-cols-3' :
    'grid-cols-2 sm:grid-cols-4';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="folding-page">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sections/inhouse')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-gray-900">Cutting & Folding</h1>
          <p className="text-xs text-gray-500">Record cutting & folding operations (multi-staff supported)</p>
        </div>
        {staffList.length > 0 && step !== 'staff' && (
          <div
            className="hidden sm:flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg"
            data-testid="staff-badge"
          >
            <Users className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">{staffList.length} staff</span>
          </div>
        )}
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">

        {/* ============ STEP: STAFF ============ */}
        {step === 'staff' && (
          <>
            <Card className="border-blue-200">
              <CardHeader className="text-center pb-2">
                <Users className="w-10 h-10 text-blue-600 mx-auto mb-2" />
                <CardTitle className="text-base">Add Staff Working on Cutting & Folding</CardTitle>
                <p className="text-xs text-gray-500 mt-1">
                  Scan or type one or more staff IDs. Qty from each bundle will be split equally across them.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <form onSubmit={handleAddStaff} className="flex gap-2">
                  <Input
                    ref={staffInputRef}
                    value={staffValue}
                    onChange={(e) => setStaffValue(e.target.value)}
                    placeholder="Scan or type Staff ID"
                    autoFocus
                    data-testid="staff-input"
                  />
                  <Button type="submit" disabled={validating || !staffValue.trim()} data-testid="staff-add-btn">
                    {validating ? '...' : (<><Plus className="w-4 h-4 mr-1" />Add</>)}
                  </Button>
                </form>

                {staffList.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs text-gray-500 font-medium">{staffList.length} staff added:</p>
                    {staffList.map((s) => (
                      <div
                        key={s.staff_id}
                        className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2"
                        data-testid={`staff-chip-${s.staff_id}`}
                      >
                        <UserCheck className="w-4 h-4 text-green-600" />
                        <span className="font-medium text-green-800 text-sm flex-1">{s.staff_name}</span>
                        <span className="text-xs text-gray-500">{s.staff_id}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          onClick={() => handleRemoveStaff(s.staff_id)}
                          data-testid={`staff-remove-${s.staff_id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleProceedToScan}
                  disabled={staffList.length === 0}
                  data-testid="proceed-to-scan-btn"
                >
                  <ScanBarcode className="w-4 h-4 mr-2" />
                  Proceed to Scan Bundles ({staffList.length} staff)
                </Button>
              </CardContent>
            </Card>

            <div className="text-center">
              <Button variant="outline" size="sm" onClick={() => navigate('/sections/inhouse/folding/history')} data-testid="view-history-btn">
                View Folding History
              </Button>
            </div>
          </>
        )}

        {/* ============ STEP: SCAN BUNDLES ============ */}
        {step === 'scan' && (
          <>
            {/* Staff info */}
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm flex-wrap">
              <Users className="w-4 h-4 text-blue-600 shrink-0" />
              {staffList.map((s, i) => (
                <React.Fragment key={s.staff_id}>
                  {i > 0 && <span className="text-gray-400">&amp;</span>}
                  <span className="font-medium">{s.staff_name}</span>
                </React.Fragment>
              ))}
            </div>

            {/* Bundle scan input */}
            <Card>
              <CardContent className="pt-4">
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Scan Bundle Numbers</label>
                <form onSubmit={handleBundleScan} className="flex gap-2">
                  <Input
                    ref={bundleInputRef}
                    value={bundleScanValue}
                    onChange={(e) => setBundleScanValue(e.target.value)}
                    placeholder="Scan bundle barcode..."
                    autoFocus
                    data-testid="bundle-scan-input"
                  />
                  <Button type="submit" disabled={scanning || !bundleScanValue.trim()} data-testid="bundle-scan-submit">
                    {scanning ? '...' : 'Add'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Scanned bundles list */}
            {scannedBundles.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Scanned Bundles ({scannedBundles.length})
                    </span>
                    <Badge variant="outline">{totalUsns} total USNs</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {scannedBundles.map((b, idx) => (
                      <div key={b.bundle_code} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 text-sm" data-testid={`scanned-bundle-${idx}`}>
                        <div>
                          <span className="font-semibold">{b.bundle_code}</span>
                          <span className="text-gray-500 ml-2">{b.design_name || ''}</span>
                          <span className="text-gray-400 ml-1">({b.usn_count} USNs)</span>
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => removeBundleAt(idx)}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  {/* Qty split preview */}
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-gray-500 font-medium mb-2">Qty split preview</p>
                    <div className={`grid gap-2 ${gridCols(splitPreview.length)}`}>
                      {splitPreview.map((s, i) => (
                        <div
                          key={s.staff_id}
                          className={`rounded-lg p-2 text-center ${SPLIT_COLORS[i % SPLIT_COLORS.length]}`}
                          data-testid={`staff-qty-${s.staff_id}`}
                        >
                          <p className="text-xs text-gray-500 truncate">{s.staff_name}</p>
                          <p className="font-bold text-gray-800">{s.qty} pcs</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Submit */}
            <Button
              className="w-full"
              size="lg"
              onClick={handleSubmit}
              disabled={submitting || scannedBundles.length === 0}
              data-testid="submit-folding-btn"
            >
              {submitting ? 'Submitting...' : (
                <>
                  <Scissors className="w-4 h-4 mr-2" />
                  Submit Folding — {scannedBundles.length} bundle{scannedBundles.length > 1 ? 's' : ''}, {totalUsns} USNs
                </>
              )}
            </Button>

            <Button variant="outline" className="w-full" onClick={() => setStep('staff')}>
              Back to Staff Selection
            </Button>
          </>
        )}

        {/* ============ STEP: RESULT ============ */}
        {step === 'result' && result && (
          <Card className="border-green-200 bg-green-50/30" data-testid="folding-result-card">
            <CardContent className="pt-6">
              <div className="text-center mb-4">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
                <h2 className="text-lg font-bold text-green-800">Folding Completed</h2>
                <p className="text-sm text-gray-600 font-mono">{result.folding_id}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Total Bundles</p>
                  <p className="text-xl font-bold">{result.total_bundles}</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Total USNs</p>
                  <p className="text-xl font-bold">{result.total_usn_count}</p>
                </div>
              </div>

              {/* Staff breakdown */}
              {(result.staff_breakdown || []).length > 0 && (
                <>
                  <p className="text-xs text-gray-500 font-medium mb-2">Staff breakdown</p>
                  <div className={`grid gap-2 ${gridCols(result.staff_breakdown.length)}`}>
                    {result.staff_breakdown.map((s, i) => (
                      <div
                        key={s.staff_id}
                        className={`rounded-lg p-3 text-center ${SPLIT_COLORS[i % SPLIT_COLORS.length]}`}
                      >
                        <p className="text-xs text-gray-500 truncate">{s.staff_name}</p>
                        <p className="text-lg font-bold text-gray-800">{s.attributed_qty} pcs</p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="mt-4 space-y-2">
                <Button className="w-full" onClick={handleReset} data-testid="new-folding-btn">
                  Start New Folding
                </Button>
                <Button variant="outline" className="w-full" onClick={() => navigate('/sections/inhouse/folding/history')}>
                  View History
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Folding;
